/**
 * IssuePreviewContext
 * 
 * Global context for managing issue/Wikipedia preview drawer on mobile.
 * Provides functionality to show preview drawer for any Wikipedia link or issue.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from "react";
import { useUser } from "util/UserContext";
import { useIssuePreview, PreviewData } from "hooks/useIssuePreview";
import { Drawer, DrawerContent } from "ui/Drawer";
import IssueCard from "components/Issues/IssueCard";
import WikipediaPreviewCard from "components/Issues/WikipediaPreviewCard";
import PostCreateButton from "ui/PostCreateButton";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { WikiSuggestion } from "util/constants";
import tippy, { Instance as TippyInstance } from "tippy.js";
import "tippy.js/dist/tippy.css";
import { LAYER_TOKENS } from "ui/layers";

/**
 * Context type definition
 */
interface IssuePreviewContextType {
  /** Show preview for a Wikipedia URL or title */
  showPreview: (wikipediaTitleOrUrl: string, clickEvent?: MouseEvent) => Promise<void>;
  /** Close the preview drawer/popover */
  closePreview: () => void;
  /** Whether preview is open */
  isOpen: boolean;
  /** Whether data is loading */
  isLoading: boolean;
}

/**
 * Provider props
 */
interface IssuePreviewProviderProps {
  children: ReactNode;
}

// Create context with undefined default
const IssuePreviewContext = createContext<IssuePreviewContextType | undefined>(undefined);

/**
 * Provider component that wraps the app and provides preview functionality
 */
export const IssuePreviewProvider: React.FC<IssuePreviewProviderProps> = ({ children }) => {
  const { userIsMobile, currentUser } = useUser();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const tippyInstanceRef = useRef<TippyInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { previewData, isLoading, fetchPreview, clearPreview } = useIssuePreview();

  /**
   * Close the preview drawer/popover
   */
  const closePreview = useCallback(() => {
    setIsOpen(false);
    
    // Destroy tippy instance if exists
    if (tippyInstanceRef.current) {
      tippyInstanceRef.current.destroy();
      tippyInstanceRef.current = null;
    }
    
    // Clear preview data after animation completes
    setTimeout(() => {
      clearPreview();
    }, 300);
  }, [clearPreview]);

  /**
   * Show preview for a Wikipedia URL or title
   */
  const showPreview = useCallback(async (wikipediaTitleOrUrl: string, clickEvent?: MouseEvent): Promise<void> => {
    if (userIsMobile) {
      setIsOpen(true);
    } else if (clickEvent && containerRef.current) {
      // Create tippy instance for desktop
      const getReferenceClientRect = () => ({
        width: 0,
        height: 0,
        top: clickEvent.clientY,
        bottom: clickEvent.clientY,
        left: clickEvent.clientX,
        right: clickEvent.clientX,
        x: clickEvent.clientX,
        y: clickEvent.clientY,
        toJSON: () => ({})
      });

      // Destroy existing instance if any
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.destroy();
      }

      tippyInstanceRef.current = tippy(document.body, {
        getReferenceClientRect,
        content: containerRef.current,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        maxWidth: 600,
        theme: "light-border",
        appendTo: () => document.body,
        zIndex: LAYER_TOKENS.previewPopover,
        onClickOutside: () => {
          closePreview();
        }
      });
    }
    
    await fetchPreview(wikipediaTitleOrUrl);
  }, [userIsMobile, fetchPreview, closePreview]);

  // Cleanup tippy on unmount
  useEffect(() => {
    return () => {
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.destroy();
      }
    };
  }, []);

  /**
   * Handle card click - navigate to appropriate page
   */
  const handleCardClick = useCallback((data: PreviewData) => {
    if (data.type === "issue") {
      // Navigate to issue page
      navigate(`/issue/${encodeURIComponent(data.data.cname)}`);
    } else {
      // Open Wikipedia mobile URL
      window.open(data.data.mobileUrl, "_blank", "noopener,noreferrer");
    }
    closePreview();
  }, [navigate, closePreview]);

  /**
   * Render mobile drawer preview content (without PostCreateButton - that's in footer)
   */
  const renderMobileDrawerContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-8 animate-spin text-eoe-primary dark:text-eoe-dark-primary" />
        </div>
      );
    }

    if (!previewData) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            No preview available
          </p>
        </div>
      );
    }

    if (previewData.type === "issue") {
      // The wrapper must be a `<div role="button">` (not `<button>`)
      // because `<IssueCard>` renders its own interactive buttons
      // (vote, comment, share, save, "more" menu trigger, read-more
      // toggles). A native `<button>` parent would produce
      // `validateDOMNesting: <button> cannot appear as a descendant
      // of <button>` warnings and break a11y for screen readers.
      return (
        <div
          role="button"
          tabIndex={0}
          className="w-full cursor-pointer rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple"
          onClick={() => handleCardClick(previewData)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCardClick(previewData);
            }
          }}
          aria-label="Open issue preview"
        >
          <IssueCard
            issue={previewData.data}
            showMeta={true}
            truncateDescription={true}
            isIssueProfilePage={false}
            showActions={false}
          />
        </div>
      );
    }

    // Wikipedia preview - without PostCreateButton (that's in footer)
    return (
      <WikipediaPreviewCard
        data={previewData.data}
        currentUser={null} // Don't show button inline, it's in footer
        onClick={() => handleCardClick(previewData)}
      />
    );
  };

  /**
   * Render mobile drawer footer with PostCreateButton for Wikipedia pages
   */
  const renderMobileDrawerFooter = () => {
    // Only show footer for Wikipedia pages with logged-in user
    if (!currentUser || !previewData || previewData.type !== "wikipedia") {
      return null;
    }

    // Convert Wikipedia data to WikiSuggestion format
    const wikiSuggestion: WikiSuggestion = {
      type: "wiki",
      name: previewData.data.title,
      link: previewData.data.wikipediaUrl,
      thumbnail: previewData.data.thumbnail || "",
      cname: previewData.data.canonicalTitle,
      subject_properties: {
        title: previewData.data.title,
        displaytitle: previewData.data.displayTitle,
        canonicalTitle: previewData.data.canonicalTitle,
        extract: previewData.data.extract,
        description: previewData.data.description,
        thumbnail: previewData.data.thumbnail,
        wikipediaUrl: previewData.data.wikipediaUrl
      },
      // Normalize nullable description to an optional string for suggestion payloads.
      short_description: previewData.data.description ?? undefined
    };

    return (
      <div 
        className="flex flex-col gap-2 px-4 py-3"
        onClick={() => setIsOpen(false)}
        data-preview-container="true"
      >
        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          Add this issue to Societea by creating a post.
        </p>
        <PostCreateButton
          buttonText="Spill the tea"
          tooltip="Add a post about this issue"
          className="w-full justify-center"
          initialIssue={wikiSuggestion as any}
        />
      </div>
    );
  };

  /**
   * Render desktop tippy content (with PostCreateButton inline)
   */
  const renderDesktopTippyContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-8 animate-spin text-eoe-primary dark:text-eoe-dark-primary" />
        </div>
      );
    }

    if (!previewData) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            No preview available
          </p>
        </div>
      );
    }

    if (previewData.type === "issue") {
      // See note in `renderMobileDrawerContent`: must be a
      // `<div role="button">` (not `<button>`) because `<IssueCard>`
      // contains its own interactive buttons. `handleCardClick` already
      // calls `closePreview()` internally, so an explicit
      // `closePreview()` here would be redundant.
      return (
        <div
          role="button"
          tabIndex={0}
          className="w-full cursor-pointer rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple"
          onClick={() => handleCardClick(previewData)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCardClick(previewData);
            }
          }}
          aria-label="Open issue preview"
        >
          <IssueCard
            issue={previewData.data}
            showMeta={true}
            truncateDescription={true}
            isIssueProfilePage={false}
            showActions={false}
          />
        </div>
      );
    }

    // Wikipedia preview - with PostCreateButton inline for desktop
    return (
      <WikipediaPreviewCard
        data={previewData.data}
        currentUser={currentUser}
        onClick={() => { handleCardClick(previewData); closePreview(); }}
        onPostCreateClick={() => closePreview()}
      />
    );
  };

  return (
    <IssuePreviewContext.Provider
      value={{
        showPreview,
        closePreview,
        isOpen: isOpen || !!tippyInstanceRef.current,
        isLoading
      }}
    >
      {children}
      
      {/* Mobile: Preview Drawer with sticky footer */}
      {userIsMobile && (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent 
            title={"Preview"}
            className="max-h-[85vh]"
            layerBase={LAYER_TOKENS.previewSurface}
            footer={renderMobileDrawerFooter()}
            stickyFooter={true}
            data-preview-container="true"
          >
            <div className="px-2 py-1" data-preview-container="true">
              {renderMobileDrawerContent()}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* Desktop: Tippy container (hidden until shown) */}
      {!userIsMobile && (
        // Important: we keep the tippy content mounted for `tippy(...)` to reference,
        // but hide it in the normal document flow so it doesn't render at the bottom
        // of every page when no preview is active.
        <div style={{ display: "none" }} aria-hidden="true">
          <div
            ref={containerRef}
            className="preview-tippy-container bg-eoe-bg dark:bg-eoe-dark-bg-modal rounded-lg shadow-lg border border-eoe-border-main dark:border-eoe-dark-border-main max-w-[600px] max-h-[600px] overflow-y-auto"
            data-preview-container="true"
          >
            <div className="p-2">
              {renderDesktopTippyContent()}
            </div>
          </div>
        </div>
      )}
    </IssuePreviewContext.Provider>
  );
};

/**
 * Hook to access issue preview context
 */
export const useIssuePreviewContext = (): IssuePreviewContextType => {
  const context = useContext(IssuePreviewContext);
  if (!context) {
    throw new Error("useIssuePreviewContext must be used within IssuePreviewProvider");
  }
  return context;
};

